/**
 * Observabilidade operacional leve (Onda 2): logs consistentes e último erro I/O em `window`
 * para diagnóstico — **sem** alterar regras de negócio.
 *
 * Filtrar na consola do browser: `Messa:IO`
 */

/** @param {unknown} err */
export function messaFirestoreErrBrief(err) {
  if (err == null) return { message: 'unknown' };
  if (typeof err !== 'object') return { message: String(err) };
  const o = /** @type {{ code?: string, message?: string, status?: number }} */ (err);
  return {
    code: o.code != null ? String(o.code) : null,
    message: o.message != null ? String(o.message) : String(err),
    status: typeof o.status === 'number' ? o.status : null,
  };
}

/**
 * @typedef {{ domain: string | null, reason: string | null, message: string | null }} GcalApiErrorItem
 * @typedef {{ httpStatus: number | null, apiCode: number | null, apiMessage: string, errors: GcalApiErrorItem[], rawSummary: string }} ParsedGapiCalendarErr
 */

/** @param {unknown} err */
function _parseGapiCalendarClientError(err) {
  if (err == null) {
    return { httpStatus: null, apiCode: null, apiMessage: 'unknown', errors: [], rawSummary: 'null' };
  }
  if (typeof err !== 'object') {
    return { httpStatus: null, apiCode: null, apiMessage: String(err), errors: [], rawSummary: String(err) };
  }
  const e = /** @type {Record<string, unknown>} */ (err);
  let httpStatus = typeof e.status === 'number' ? e.status : null;
  let apiCode = null;
  let apiMessage = '';
  /** @type {GcalApiErrorItem[]} */
  let errors = [];

  const result = e.result;
  if (result && typeof result === 'object') {
    const apiErr = /** @type {{ code?: number, message?: string, errors?: unknown[] }} */ (
      /** @type {Record<string, unknown>} */ (result).error
    );
    if (apiErr && typeof apiErr === 'object') {
      if (typeof apiErr.code === 'number') apiCode = apiErr.code;
      if (typeof apiErr.message === 'string') apiMessage = apiErr.message.trim();
      if (Array.isArray(apiErr.errors)) {
        errors = apiErr.errors.map((x) => {
          if (!x || typeof x !== 'object') {
            return { domain: null, reason: null, message: null };
          }
          const o = /** @type {Record<string, unknown>} */ (x);
          return {
            domain: o.domain != null ? String(o.domain) : null,
            reason: o.reason != null ? String(o.reason) : null,
            message: o.message != null ? String(o.message) : null,
          };
        });
      }
    }
  }

  if ((!apiMessage || errors.length === 0) && typeof e.body === 'string' && e.body.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(e.body);
      const apiErr = parsed && typeof parsed === 'object' ? /** @type {Record<string, unknown>} */ (parsed).error : null;
      if (apiErr && typeof apiErr === 'object') {
        const ae = /** @type {{ code?: number, message?: string, errors?: unknown[] }} */ (apiErr);
        if (apiCode == null && typeof ae.code === 'number') apiCode = ae.code;
        if (!apiMessage && typeof ae.message === 'string') apiMessage = ae.message.trim();
        if (errors.length === 0 && Array.isArray(ae.errors)) {
          errors = ae.errors.map((x) => {
            if (!x || typeof x !== 'object') return { domain: null, reason: null, message: null };
            const o = /** @type {Record<string, unknown>} */ (x);
            return {
              domain: o.domain != null ? String(o.domain) : null,
              reason: o.reason != null ? String(o.reason) : null,
              message: o.message != null ? String(o.message) : null,
            };
          });
        }
      }
    } catch {
      /* ignore */
    }
  }

  if (httpStatus == null && apiCode != null) httpStatus = apiCode;

  if (!apiMessage) {
    if (typeof e.message === 'string' && e.message.trim()) apiMessage = e.message.trim();
    else {
      try {
        apiMessage = JSON.stringify(e).slice(0, 800);
      } catch {
        apiMessage = '[unserializable error]';
      }
    }
  }

  const reasonStr = errors
    .map((r) => r.reason)
    .filter(Boolean)
    .join(',');
  const rawSummary = [httpStatus != null ? `HTTP ${httpStatus}` : '', apiMessage, reasonStr].filter(Boolean).join(' | ');

  return { httpStatus, apiCode, apiMessage, errors, rawSummary };
}

/**
 * Quota / rate limit (Calendar API). Prioridade: `rawSummary` e mensagem — o cliente gapi por vezes
 * não preenche `errors[]` como esperado, mas o resumo textual já traz `rateLimitExceeded`.
 * @param {ParsedGapiCalendarErr} parts
 * @returns {'rateLimitExceeded'|'userRateLimitExceeded'|'quotaExceeded'|'rate_limited'|null}
 */
function _calendarUsageLimitClassification(parts) {
  const status = parts.httpStatus ?? parts.apiCode;
  const msg = (parts.apiMessage || '').toLowerCase();
  const raw = (parts.rawSummary || '').toLowerCase();

  /** `rawSummary` costuma ser `HTTP 403 | Rate Limit Exceeded | rateLimitExceeded` */
  if (raw.includes('userratelimitexceeded')) return 'userRateLimitExceeded';
  if (raw.includes('quotaexceeded')) return 'quotaExceeded';
  if (raw.includes('ratelimitexceeded')) return 'rateLimitExceeded';
  if (
    raw.includes('rate limit') ||
    raw.includes('rate_limit') ||
    raw.includes('too many requests') ||
    (raw.includes('quota') && (raw.includes('exceed') || raw.includes('exceeded')))
  ) {
    if (status === 403 || status === 429) return 'rate_limited';
  }

  if (msg.includes('userratelimitexceeded')) return 'userRateLimitExceeded';
  if (msg.includes('quotaexceeded')) return 'quotaExceeded';
  if (msg.includes('ratelimitexceeded')) return 'rateLimitExceeded';
  if (msg.includes('user rate limit') || msg.includes('user-rate')) {
    if (status === 403 || status === 429) return 'userRateLimitExceeded';
  }
  if (msg.includes('rate limit') || msg.includes('rate_limit') || msg.includes('too many requests')) {
    if (status === 403 || status === 429) return 'rate_limited';
  }
  if (msg.includes('quota') && (msg.includes('exceed') || msg.includes('exceeded'))) {
    if (status === 403 || status === 429) return 'rate_limited';
  }
  if ((msg.includes('rate') && msg.includes('exceed')) || msg.includes('ratelimit')) {
    if (status === 403 || status === 429) return 'rate_limited';
  }

  const reasonTokens = (parts.errors || [])
    .map((r) => (r.reason || '').trim().toLowerCase())
    .filter(Boolean);
  if (reasonTokens.includes('userratelimitexceeded')) return 'userRateLimitExceeded';
  if (reasonTokens.includes('quotaexceeded')) return 'quotaExceeded';
  if (reasonTokens.includes('ratelimitexceeded')) return 'rateLimitExceeded';
  for (const r of parts.errors || []) {
    const reason = (r.reason || '').toLowerCase();
    if (reason === 'userratelimitexceeded') return 'userRateLimitExceeded';
    if (reason === 'quotaexceeded') return 'quotaExceeded';
    if (reason === 'ratelimitexceeded') return 'rateLimitExceeded';
  }
  if (status === 429) return 'rate_limited';
  return null;
}

/**
 * @param {ParsedGapiCalendarErr} parts
 * @returns {'rateLimitExceeded'|'userRateLimitExceeded'|'quotaExceeded'|'rate_limited'|'permission_denied'|'organizer_only'|'event_not_found'|'calendar_not_writable'|'insufficient_scope_or_token'|'other_forbidden'|'unknown'}
 */
function _classifyGoogleCalendarFailure(parts) {
  const { httpStatus, apiCode, apiMessage, errors } = parts;
  const status = httpStatus ?? apiCode;
  const msg = (apiMessage || '').toLowerCase();
  const reasons = errors
    .map((r) => (r.reason || '').toLowerCase())
    .join(' ');
  const domains = errors
    .map((r) => (r.domain || '').toLowerCase())
    .join(' ');
  const errMsgs = errors
    .map((r) => (r.message || '').toLowerCase())
    .join(' ');
  const blob = `${msg} ${reasons} ${domains} ${errMsgs}`;

  const usageClass = _calendarUsageLimitClassification(parts);
  if (usageClass) return usageClass;

  if (status === 404 || status === 410 || reasons.includes('notfound') || blob.includes('not found')) {
    return 'event_not_found';
  }
  if (
    status === 401 ||
    reasons.includes('autherror') ||
    blob.includes('invalid_credentials') ||
    blob.includes('invalid_grant') ||
    (blob.includes('oauth') && blob.includes('token'))
  ) {
    return 'insufficient_scope_or_token';
  }
  if (blob.includes('insufficient') && (blob.includes('authentication') || blob.includes('scope'))) {
    return 'insufficient_scope_or_token';
  }

  if (status === 403) {
    if (
      blob.includes('organizer') ||
      blob.includes('cannot_change') ||
      blob.includes('only the organizer') ||
      reasons.includes('cannotchangeorganizer')
    ) {
      return 'organizer_only';
    }
    if (
      blob.includes('not a writer') ||
      blob.includes('read-only') ||
      blob.includes('read only') ||
      blob.includes('writer access') ||
      reasons.includes('requiredaccesslevel') ||
      (msg.includes('cannot modify') && blob.includes('calendar'))
    ) {
      return 'calendar_not_writable';
    }
    if (reasons.includes('useraccessdenied') || reasons.includes('domainpolicy')) {
      return 'permission_denied';
    }
    if (reasons.includes('forbidden') || blob.includes('forbidden')) {
      return 'permission_denied';
    }
    return 'other_forbidden';
  }

  return 'unknown';
}

const _GCAL_RATE_CLASSIFICATIONS = new Set([
  'rate_limited',
  'rateLimitExceeded',
  'userRateLimitExceeded',
  'quotaExceeded',
]);

/**
 * Agrupa `classification` para filtros de log (sync agenda / suporte).
 * @param {string | null | undefined} classification
 * @returns {'rate_limited'|'permission_denied'|'organizer_only'|'other_forbidden'|null}
 */
export function messaGoogleCalendarFailureBucket(classification) {
  const c = classification != null ? String(classification) : '';
  if (_GCAL_RATE_CLASSIFICATIONS.has(c)) return 'rate_limited';
  if (c === 'organizer_only') return 'organizer_only';
  if (c === 'permission_denied' || c === 'calendar_not_writable') return 'permission_denied';
  if (c === 'other_forbidden') return 'other_forbidden';
  return null;
}

/**
 * Metadados para retry de quota/rate limit (Calendar API).
 * @param {unknown} err
 */
export function messaGoogleCalendarRateLimitMeta(err) {
  const parts = _parseGapiCalendarClientError(err);
  const classification = _classifyGoogleCalendarFailure(parts);
  const retryable = parts.httpStatus === 429 || _GCAL_RATE_CLASSIFICATIONS.has(classification);
  return { parts, classification, retryable };
}

/**
 * Erro do `gapi.client.calendar` (Calendar API v3): extrai corpo `result.error`, lista `errors[]`
 * e classifica falhas comuns (403, 404, 401). Compatível com `messaRecordLastIoError` via `code` / `message` / `status`.
 *
 * @param {unknown} err — rejeição do cliente Google APIs
 * @param {{ calendarId?: string | null, eventId?: string | null, itemId?: string | null, operation?: string }} [ctx]
 */
export function messaGoogleCalendarApiErrBrief(err, ctx = {}) {
  const parts = _parseGapiCalendarClientError(err);
  const classification = _classifyGoogleCalendarFailure(parts);
  const { calendarId = null, eventId = null, itemId = null, operation = 'gcal' } = ctx;

  const lineMessage = [
    `[${operation}]`,
    classification,
    parts.httpStatus != null ? `HTTP ${parts.httpStatus}` : null,
    parts.apiMessage || null,
    calendarId ? `calendarId=${calendarId}` : null,
    eventId != null && eventId !== '' ? `eventId=${eventId}` : null,
    itemId != null && itemId !== '' ? `itemId=${itemId}` : null,
  ]
    .filter(Boolean)
    .join(' | ');

  const gcalFailureBucket = messaGoogleCalendarFailureBucket(classification);

  return {
    operation,
    calendarId,
    eventId,
    itemId,
    httpStatus: parts.httpStatus,
    apiCode: parts.apiCode,
    apiMessage: parts.apiMessage,
    errors: parts.errors,
    classification,
    gcalFailureBucket,
    rawSummary: parts.rawSummary,
    /** Compatível com messaFirestoreErrBrief / __MESSA_LAST_IO_ERROR */
    code: `gcal:${classification}`,
    message: lineMessage,
    status: parts.httpStatus,
  };
}

/**
 * Regista último erro para tooltip do sync-dot e suporte (TTL implícito no consumidor).
 * @param {string} area
 * @param {unknown} err
 */
/** @param {unknown} err */
function _gcalDiagnosticExtra(err) {
  if (!err || typeof err !== 'object') return {};
  const o = /** @type {Record<string, unknown>} */ (err);
  if (o.classification == null || !Array.isArray(o.errors)) return {};
  return {
    gcalClassification: o.classification,
    gcalErrors: o.errors,
    gcalApiMessage: o.apiMessage,
    gcalCalendarId: o.calendarId,
    gcalEventId: o.eventId,
    gcalItemId: o.itemId,
    gcalHttpStatus: o.httpStatus,
    gcalApiCode: o.apiCode,
    gcalOperation: o.operation,
    ...(o.gcalUpdatePayloadSummary != null ? { gcalUpdatePayloadSummary: o.gcalUpdatePayloadSummary } : {}),
    ...(o.updateMode != null ? { updateMode: o.updateMode } : {}),
    ...(o.result != null ? { result: o.result } : {}),
    ...(o.gcalFailureBucket != null ? { gcalFailureBucket: o.gcalFailureBucket } : {}),
  };
}

export function messaRecordLastIoError(area, err) {
  if (typeof window === 'undefined') return;
  try {
    const b = messaFirestoreErrBrief(err);
    window.__MESSA_LAST_IO_ERROR = {
      area,
      at: Date.now(),
      iso: new Date().toISOString(),
      ...b,
      ..._gcalDiagnosticExtra(err),
    };
  } catch {
    /* ignore */
  }
}

/**
 * @param {string} area — ex.: data-main, import, gcal-sync, finance-month
 * @param {'info'|'warn'|'error'} level
 * @param {string} message
 * @param {unknown} [detail]
 */
export function messaOpLog(area, level, message, detail) {
  const prefix = `[Messa:IO:${area}]`;
  if (typeof window !== 'undefined') {
    try {
      if (!Array.isArray(window.__MESSA_OP_LOG_RECENT)) window.__MESSA_OP_LOG_RECENT = [];
      window.__MESSA_OP_LOG_RECENT.push({
        at: Date.now(),
        iso: new Date().toISOString(),
        area,
        level,
        message,
        detail,
      });
      if (window.__MESSA_OP_LOG_RECENT.length > 200) {
        window.__MESSA_OP_LOG_RECENT = window.__MESSA_OP_LOG_RECENT.slice(-200);
      }
    } catch {
      /* ignore */
    }
  }
  if (level === 'error') {
    if (detail !== undefined) console.error(prefix, message, detail);
    else console.error(prefix, message);
    messaRecordLastIoError(area, detail != null ? detail : new Error(message));
  } else if (level === 'warn') {
    if (detail !== undefined) console.warn(prefix, message, detail);
    else console.warn(prefix, message);
  } else if (level === 'debug') {
    // nível debug: só grava no buffer em memória, sem console
  } else {
    if (detail !== undefined) console.info(prefix, message, detail);
    else console.info(prefix, message);
  }
}
